import React from 'react';

import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { EditorialQuote as EditorialQuoteContent } from '../../types/editorial-quote.type';

interface EditorialQuoteProps {
  content: EditorialQuoteContent;
  /** Sur une bande de couleur pleine, le texte passe en blanc. */
  onSolid?: boolean;
  /**
   * `side` place le portrait à côté du texte, pour une citation seule en pleine largeur.
   * `stacked` le place au-dessus, pour une carte de grille.
   */
  orientation?: 'side' | 'stacked';
}

const useStyles = makeStyles((theme) => ({
  // Dimensions carrées explicites plutôt que `aspect-ratio`, pour la même raison que dans
  // `ResponsiveVideo` : la propriété manque aux navigateurs que ce site sert encore.
  portrait: {
    display: 'block',
    width: 220,
    height: 220,
    maxWidth: '100%',
    margin: '0 auto',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  portraitStacked: {
    width: 132,
    height: 132,
  },
  quote: {
    fontFamily: 'Bitter, serif',
    fontWeight: 400,
    fontStyle: 'italic',
    fontSize: 'clamp(1.05rem, 2.2vw, 1.35rem)',
    lineHeight: 1.55,
    margin: 0,
    color: theme.palette.client.primary.extraDark,
  },
  // Cent mots en italique de 1,35 rem dans une demi-colonne écraseraient la lecture, là où une
  // citation seule et pleine largeur les porte bien.
  quoteStacked: {
    fontSize: '0.98rem',
    lineHeight: 1.6,
  },
  author: {
    display: 'block',
    marginTop: '1.25rem',
    fontWeight: 600,
    fontStyle: 'normal',
  },
  role: {
    fontSize: '0.9rem',
    opacity: 0.8,
  },
  onSolid: {
    color: '#fff',
  },
  // En carte de grille, `height: 100%` aligne le bas des quatre témoignages malgré des longueurs
  // inégales, et l'attribution est poussée en bas par `marginTop: auto`.
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.25rem',
    padding: '2rem 1.5rem',
    borderRadius: 12,
    backgroundColor: '#fff',
    border: '1px solid rgba(102, 118, 29, 0.16)',
    textAlign: 'center',
  },
  cardQuote: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
  },
  cardAttribution: {
    marginTop: 'auto',
    paddingTop: '1.25rem',
  },
}));

const EditorialQuote: React.FC<EditorialQuoteProps> = ({ content, onSolid = false, orientation = 'side' }) => {
  const classes = useStyles();
  const solid = onSolid ? classes.onSolid : '';

  const portrait = (
    <img
      className={`${classes.portrait} ${orientation === 'stacked' ? classes.portraitStacked : ''}`}
      src={content.portrait}
      alt={content.portraitAlt}
      loading="lazy"
    />
  );

  // `blockquote` et `cite` disent la citation et son auteur, ce qu'aucune classe ne peut faire.
  // Les deux orientations partagent cette structure : seul l'agencement change.
  const attribution = (
    <footer className={orientation === 'stacked' ? classes.cardAttribution : ''}>
      <Typography component="cite" className={`${classes.author} ${solid}`}>
        {content.author}
      </Typography>
      <Typography className={`${classes.role} ${solid}`}>{content.role}</Typography>
    </footer>
  );

  if (orientation === 'stacked') {
    return (
      <article className={classes.card}>
        {portrait}
        <blockquote className={`${classes.quote} ${classes.quoteStacked} ${classes.cardQuote} ${solid}`}>
          {`« ${content.quote} »`}
          {attribution}
        </blockquote>
      </article>
    );
  }

  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} sm={4} md={3}>
        {portrait}
      </Grid>

      <Grid item xs={12} sm={8} md={9}>
        <blockquote className={`${classes.quote} ${solid}`}>
          {`« ${content.quote} »`}
          {attribution}
        </blockquote>
      </Grid>
    </Grid>
  );
};

export default EditorialQuote;
